import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Shield, ShieldAlert, ShieldCheck, ClipboardList, Filter as FilterIcon, EyeOff, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = { OPEN: "Open", REVIEWING: "Under Review", RESOLVED: "Resolved", DISMISSED: "Dismissed" };
const TYPE_LABEL = { post: "Circle Post", comment: "Circle Comment", user: "Member Profile", dm_message: "Direct Message", mentor: "Mentor Profile" };
const REASON_LABEL = {
  HARASSMENT: "Harassment", SPAM: "Spam", THREATS: "Threats", IMPERSONATION: "Impersonation",
  ACADEMIC_MISCONDUCT: "Academic misconduct", INAPPROPRIATE_CONTENT: "Inappropriate content",
  OFF_TOPIC: "Off-topic", OTHER: "Other",
};

function StatusBadge({ status }) {
  const map = {
    OPEN: "bg-amber-50 text-amber-800 border-amber-200",
    REVIEWING: "bg-blue-50 text-blue-800 border-blue-200",
    RESOLVED: "bg-emerald-50 text-emerald-800 border-emerald-200",
    DISMISSED: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return (
    <span data-testid={`report-status-${status}`}
      className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[11px] font-medium ${map[status] || ""}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function SummaryCard({ label, value, icon: Icon, testid, tone = "default" }) {
  const toneCls = {
    default: "border-border",
    warn: "border-amber-200 bg-amber-50/40",
    info: "border-blue-200 bg-blue-50/40",
    ok: "border-emerald-200 bg-emerald-50/40",
    mute: "border-slate-200 bg-slate-50/40",
  }[tone];
  return (
    <Card data-testid={testid} className={`p-4 ${toneCls}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</p>
          <p className="mt-1 text-2xl font-serif font-black">{value}</p>
        </div>
        {Icon ? <Icon className="h-5 w-5 text-muted-foreground" /> : null}
      </div>
    </Card>
  );
}

export default function Moderation() {
  const { user } = useAuth();
  const canAccess = useMemo(() => user && (user.role === "admin" || user.role === "faculty"), [user]);

  const [summary, setSummary] = useState(null);
  const [reports, setReports] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [filterStatus, setFilterStatus] = useState("OPEN");
  const [filterType, setFilterType] = useState("");
  const [filterReason, setFilterReason] = useState("");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState(null);
  const [dmBody, setDmBody] = useState(null);

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await api.get("/trust-center/summary");
      setSummary(data);
    } catch (e) {
      if (e.response?.status === 403) return; // will render forbidden state
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, []);

  const loadReports = useCallback(async (reset = true) => {
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set("status", filterStatus);
      if (filterType) params.set("content_type", filterType);
      if (filterReason) params.set("reason", filterReason);
      if (search.trim()) params.set("q", search.trim());
      if (!reset && nextCursor) params.set("cursor", nextCursor);
      params.set("limit", "20");
      const { data } = await api.get(`/trust-center/reports?${params.toString()}`);
      setReports(reset ? data.results : [...reports, ...data.results]);
      setNextCursor(data.next_cursor);
      setHasMore(data.has_more);
    } catch (e) {
      if (e.response?.status !== 403) toast.error(formatApiError(e.response?.data?.detail));
    }
  }, [filterStatus, filterType, filterReason, search, nextCursor, reports]);

  useEffect(() => { if (canAccess) { loadSummary(); loadReports(true); } }, [canAccess, filterStatus, filterType, filterReason]);

  const openReport = async (rid) => {
    setOpenId(rid); setDetail(null); setRevealed(null); setDmBody(null);
    try {
      const { data } = await api.get(`/trust-center/reports/${rid}`);
      setDetail(data);
      setNotes(data.report.moderation_notes || "");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setOpenId(null);
    }
  };

  const changeStatus = async (next) => {
    if (!detail) return;
    setBusy(true);
    try {
      await api.patch(`/trust-center/reports/${detail.report.id}`,
        { status: next, moderation_notes: notes || undefined });
      toast.success(`Report ${STATUS_LABEL[next]?.toLowerCase() || next}`);
      await openReport(detail.report.id);
      await Promise.all([loadSummary(), loadReports(true)]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveNotes = async () => {
    if (!detail) return;
    setBusy(true);
    try {
      await api.patch(`/trust-center/reports/${detail.report.id}`, { moderation_notes: notes });
      toast.success("Notes saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const removeContent = async () => {
    if (!detail?.target || !["post", "comment"].includes(detail.report.content_type)) return;
    setBusy(true);
    try {
      await api.post(`/trust-center/content/${detail.report.content_type}/${detail.target.id}/remove`,
        { reason: `Report ${detail.report.id}` });
      toast.success("Content removed");
      await openReport(detail.report.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const restoreContent = async () => {
    if (!detail?.target || !["post", "comment"].includes(detail.report.content_type)) return;
    setBusy(true);
    try {
      await api.post(`/trust-center/content/${detail.report.content_type}/${detail.target.id}/restore`,
        { reason: `Report ${detail.report.id}` });
      toast.success("Content restored");
      await openReport(detail.report.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const revealAuthor = async () => {
    if (!detail?.target?.is_anonymous) return;
    if (!window.confirm("Reveal anonymous author? This action is audit-logged.")) return;
    try {
      const { data } = await api.get(
        `/moderation/anon-author/${detail.report.content_type}/${detail.target.id}`);
      setRevealed(data);
      toast.info("Author identity revealed (audit event recorded)");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const fetchDmBody = async () => {
    if (!detail || detail.report.content_type !== "dm_message") return;
    if (!window.confirm("Access reported DM content? Every access is audit-logged.")) return;
    try {
      const { data } = await api.get(`/moderation/reported-message/${detail.report.id}`);
      setDmBody(data);
      toast.info("Reported message loaded (audit event recorded)");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!user) return null;
  if (!canAccess) {
    return (
      <div className="max-w-2xl mx-auto p-8" data-testid="moderation-forbidden">
        <Card className="p-8 text-center">
          <ShieldAlert className="h-8 w-8 mx-auto text-muted-foreground" />
          <h1 className="mt-3 font-serif text-2xl font-black">Trust & Safety</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            This section is available only to platform administrators.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="moderation-page">
      <header className="flex items-center gap-3">
        <Shield className="h-6 w-6 text-primary" />
        <div>
          <h1 className="font-serif text-3xl font-black tracking-tight">Trust &amp; Safety</h1>
          <p className="text-sm text-muted-foreground">Review reported content, protect the community, act with restraint.</p>
        </div>
      </header>

      {/* Dashboard */}
      <section className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="moderation-summary">
        <SummaryCard label="Open" value={summary?.by_status?.OPEN ?? 0} icon={ClipboardList} tone="warn" testid="sum-open" />
        <SummaryCard label="Under Review" value={summary?.by_status?.REVIEWING ?? 0} icon={FilterIcon} tone="info" testid="sum-reviewing" />
        <SummaryCard label="Resolved" value={summary?.by_status?.RESOLVED ?? 0} icon={ShieldCheck} tone="ok" testid="sum-resolved" />
        <SummaryCard label="Dismissed" value={summary?.by_status?.DISMISSED ?? 0} icon={ShieldAlert} tone="mute" testid="sum-dismissed" />
        <SummaryCard label="Active Total" value={summary?.total_active ?? 0} icon={Shield} tone="default" testid="sum-total" />
      </section>

      {/* Filters */}
      <Card className="p-4" data-testid="moderation-filters">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex gap-1">
            {["OPEN", "REVIEWING", "RESOLVED", "DISMISSED", ""].map(s => (
              <Button key={s || "all"} size="sm"
                variant={filterStatus === s ? "default" : "outline"}
                data-testid={`filter-status-${s || "all"}`}
                onClick={() => setFilterStatus(s)}>
                {s ? STATUS_LABEL[s] : "All"}
              </Button>
            ))}
          </div>
          <select data-testid="filter-type" className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="">All types</option>
            <option value="post">Circle Post</option>
            <option value="comment">Circle Comment</option>
            <option value="user">Member Profile</option>
            <option value="dm_message">Direct Message</option>
            <option value="mentor">Mentor Profile</option>
          </select>
          <select data-testid="filter-reason" className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            value={filterReason} onChange={(e) => setFilterReason(e.target.value)}>
            <option value="">All reasons</option>
            {Object.entries(REASON_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <Input data-testid="filter-search" className="h-9 max-w-xs" placeholder="Report ID or content ID"
            value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") loadReports(true); }} />
          <Button size="sm" variant="outline" data-testid="filter-apply" onClick={() => loadReports(true)}>Apply</Button>
        </div>
      </Card>

      {/* Queue + Detail */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <Card className="lg:col-span-2 p-0 overflow-hidden" data-testid="report-queue">
          <div className="border-b p-3 text-xs uppercase tracking-widest text-muted-foreground">
            {reports.length} report{reports.length === 1 ? "" : "s"}
          </div>
          {reports.length === 0 ? (
            <div className="p-8 text-center" data-testid="queue-empty">
              <ShieldCheck className="h-8 w-8 mx-auto text-emerald-500" />
              <p className="mt-2 text-sm font-medium">No {filterStatus ? STATUS_LABEL[filterStatus].toLowerCase() : ""} reports.</p>
              <p className="text-xs text-muted-foreground">DocCircle currently has nothing requiring review here.</p>
            </div>
          ) : (
            <ul className="divide-y">
              {reports.map(r => (
                <li key={r.id}>
                  <button data-testid={`queue-item-${r.id}`}
                    onClick={() => openReport(r.id)}
                    className={`w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors ${openId === r.id ? "bg-muted/70" : ""}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{TYPE_LABEL[r.content_type] || r.content_type}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {REASON_LABEL[r.reason] || r.reason} · {new Date(r.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <StatusBadge status={r.status} />
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {hasMore && (
            <div className="p-3 border-t text-center">
              <Button size="sm" variant="outline" data-testid="queue-load-more" onClick={() => loadReports(false)}>Load more</Button>
            </div>
          )}
        </Card>

        <Card className="lg:col-span-3 p-5" data-testid="report-detail">
          {!openId ? (
            <div className="text-center py-12">
              <ClipboardList className="h-8 w-8 mx-auto text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">Select a report to review.</p>
            </div>
          ) : !detail ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <div className="space-y-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Report</p>
                  <h2 className="font-serif text-xl font-black" data-testid="detail-title">
                    {TYPE_LABEL[detail.report.content_type]} · {REASON_LABEL[detail.report.reason] || detail.report.reason}
                  </h2>
                  <p className="text-xs text-muted-foreground mt-1">
                    Reported {new Date(detail.report.created_at).toLocaleString()}
                  </p>
                </div>
                <StatusBadge status={detail.report.status} />
              </div>

              {detail.report.description && (
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Reporter description</p>
                  <p className="mt-1 text-sm whitespace-pre-wrap" data-testid="detail-description">{detail.report.description}</p>
                </div>
              )}

              {detail.circle && (
                <div className="text-xs text-muted-foreground">
                  Circle: <span className="font-medium text-foreground">{detail.circle.name}</span> · {detail.circle.category}
                </div>
              )}

              {/* Target payload */}
              <div className="rounded-lg border p-4 bg-muted/30" data-testid="detail-target">
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Reported content</p>
                {!detail.target ? (
                  <p className="text-sm italic text-muted-foreground">Target no longer exists.</p>
                ) : detail.report.content_type === "user" ? (
                  <div className="text-sm space-y-1">
                    <p><span className="text-muted-foreground">Display name:</span> {detail.target.display_name}</p>
                    <p><span className="text-muted-foreground">Degree:</span> {detail.target.degree_type || "—"} · {detail.target.discipline || "—"}</p>
                    <p><span className="text-muted-foreground">Account status:</span> {detail.target.account_status}</p>
                  </div>
                ) : detail.report.content_type === "dm_message" ? (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">{detail.target.note}</p>
                    <Button size="sm" variant="outline" data-testid="detail-fetch-dm" onClick={fetchDmBody}>
                      Access reported message
                    </Button>
                    {dmBody && (
                      <div className="mt-2 rounded-md border bg-background p-3" data-testid="dm-body">
                        <p className="text-[11px] uppercase text-muted-foreground">Reported message</p>
                        <p className="mt-1 text-sm whitespace-pre-wrap">{dmBody.message.body}</p>
                        <p className="mt-2 text-[11px] text-muted-foreground">
                          Sender: {dmBody.message.sender_id} · {new Date(dmBody.message.created_at).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <p className="text-sm whitespace-pre-wrap" data-testid="detail-body">{detail.target.body}</p>
                    <p className="mt-2 text-[11px] text-muted-foreground">
                      Status: {detail.target.status || "ACTIVE"}
                      {detail.target.is_anonymous ? " · Anonymous" : ""}
                    </p>
                    {detail.target.is_anonymous && (
                      <div className="mt-2 space-y-1">
                        {revealed ? (
                          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                            Revealed author: {revealed.author_name || revealed.author_id}
                          </p>
                        ) : (
                          <Button size="sm" variant="outline" data-testid="detail-reveal-anon" onClick={revealAuthor}>
                            <EyeOff className="h-3 w-3 mr-1" /> Reveal author (audit-logged)
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Moderation notes */}
              <div>
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Internal moderation notes</p>
                <Textarea data-testid="detail-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                  placeholder="Not visible to members. Document decisions here." className="mt-1" />
                <div className="mt-2">
                  <Button size="sm" variant="outline" data-testid="save-notes" onClick={saveNotes} disabled={busy}>Save notes</Button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 border-t pt-4">
                {detail.report.status === "OPEN" && (
                  <Button size="sm" data-testid="action-review" disabled={busy} onClick={() => changeStatus("REVIEWING")}>Start review</Button>
                )}
                {detail.report.status === "REVIEWING" && (
                  <>
                    <Button size="sm" data-testid="action-resolve" disabled={busy} onClick={() => changeStatus("RESOLVED")}>Mark resolved</Button>
                    <Button size="sm" variant="outline" data-testid="action-dismiss" disabled={busy} onClick={() => changeStatus("DISMISSED")}>Dismiss</Button>
                  </>
                )}
                {detail.report.status === "OPEN" && (
                  <Button size="sm" variant="outline" data-testid="action-dismiss-open" disabled={busy} onClick={() => changeStatus("DISMISSED")}>Dismiss without review</Button>
                )}
                {["RESOLVED", "DISMISSED"].includes(detail.report.status) && (
                  <Button size="sm" variant="outline" data-testid="action-reopen" disabled={busy} onClick={() => changeStatus("OPEN")}>Reopen</Button>
                )}
                {["post", "comment"].includes(detail.report.content_type) && detail.target && (
                  detail.target.status === "REMOVED" ? (
                    <Button size="sm" variant="outline" data-testid="action-restore" disabled={busy} onClick={restoreContent}>Restore content</Button>
                  ) : (
                    <Button size="sm" variant="destructive" data-testid="action-remove" disabled={busy} onClick={removeContent}>Remove content</Button>
                  )
                )}
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
